const KEY = (zoneId: string) => `saltseed.hidden-senders.${zoneId}`

/**
 * Which senders this browser has chosen not to read, PER ZONE.
 *
 * Client-side and never sent to the server. This is "what I want to look at", not a permission:
 * the history, the jump rail and the CSV export stay complete, so nothing is lost and un-ticking
 * restores the view. Making it a query parameter would put a display preference into the API and
 * make pagination counts depend on who is hidden.
 *
 * Per zone, not per page: an agent you have chosen not to look at should not reappear because you
 * opened a channel. Both the messages page and the channel conversation view read this.
 *
 * **Always returns a `string[]`.** `localStorage` is user-writable and survives across app
 * versions, so the stored value can be almost anything: hand-edited, left over from an older key
 * shape, or simply corrupted. `JSON.parse` only rejects text that fails to *parse* — `'{}'` and
 * `'42'` parse fine and are not arrays, and `(... as string[])` is a compile-time claim the runtime
 * does not check. Feeding a non-array into `visibleMessages`/`toggleSender`, which both call
 * `.includes(...)` on it unconditionally, threw a `TypeError` and broke the messages page for that
 * zone until the user cleared site data — exactly the failure this module otherwise avoids
 * everywhere else. So: anything that is not a JSON array is treated as "hide nothing", and any
 * entry inside an otherwise-valid array that is not a string is dropped rather than trusted (a
 * stray number would never crash anything, but it would sit in the list forever silently matching
 * no sender).
 */
export function hiddenSenders(zoneId: string): string[] {
  try {
    const raw = localStorage.getItem(KEY(zoneId))
    if (raw === null) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((entry): entry is string => typeof entry === 'string')
  } catch {
    // A private window, cleared site data, or storage the browser refuses: show everything.
    return []
  }
}

export function toggleSender(zoneId: string, name: string): string[] {
  const current = hiddenSenders(zoneId)
  const next = current.includes(name) ? current.filter((n) => n !== name) : [...current, name]
  try {
    localStorage.setItem(KEY(zoneId), JSON.stringify(next))
  } catch {
    // Not fatal: the filter still applies to this render, it just will not survive a reload.
  }
  return next
}

/**
 * Un-hides everyone at once, and returns the new list: always `[]`.
 *
 * **The result is computed, not read back out of storage.** "Show all" used to be a loop of
 * `toggleSender` calls whose return value was threaded through the iterations — which is correct
 * only while every write lands. When `setItem` throws (a private window, a full quota) `toggleSender`
 * swallows it by design, so each iteration re-read the *unchanged* stored list and un-hid exactly one
 * name; the button appeared to bring back only the last sender. The rest of this module already
 * degrades gracefully on that throw, and a control whose whole job is "clear the filter" must not be
 * the one place that depends on the write succeeding.
 */
export function showAllSenders(zoneId: string): string[] {
  try {
    localStorage.removeItem(KEY(zoneId))
  } catch {
    // Same bargain as `toggleSender`: the filter is cleared for this render either way, it just
    // will not survive a reload.
  }
  return []
}

export function visibleMessages<T extends { sender: string }>(messages: T[], hidden: string[]): T[] {
  return hidden.length === 0 ? messages : messages.filter((m) => !hidden.includes(m.sender))
}
